import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAppState } from './useAppStatePreservation';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  ensureNotCompletedOrPurge,
  syncCompletedLevelsFromDB,
  getCurrentUserIdSync,
} from '@/lib/level-completion-guard';

/**
 * Hook para restaurar el estado de la aplicación al recargar
 * Restaura la ruta Y la posición de scroll
 */
export const useAppStateRestoration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasRestoredRef = useRef(false);
  const scrollRestoredRef = useRef(false);
  const hasShownToastRef = useRef(false);
  const { t } = useLanguage();

  // Restaurar scroll position cuando cambia la ruta
  useEffect(() => {
    if (scrollRestoredRef.current) return;

    const savedState = getAppState();
    const currentPath = location.pathname;
    
    // Restaurar scroll position para la ruta actual
    // Dashboard siempre debe abrir en el top: restaurar un offset guardado deja
    // el header (logo + saludo) por debajo de la status bar de iOS.
    const isTopAnchoredRoute = currentPath === '/dashboard';
    const scrollPosition = isTopAnchoredRoute ? 0 : (savedState.scrollPositions?.[currentPath] || 0);

    if (scrollPosition > 0) {
      // Dar tiempo para que el contenido se renderice
      const scrollTimer = setTimeout(() => {
        console.log('📜 [Restoration] Restoring scroll to:', scrollPosition, 'for route:', currentPath);
        window.scrollTo({
          top: scrollPosition,
          behavior: 'instant' as ScrollBehavior
        });
        scrollRestoredRef.current = true;
      }, 150);
      
      return () => clearTimeout(scrollTimer);
    } else {
      // Asegurar que scroll esté en el top si no hay posición guardada
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    
    // CRITICAL: NO restaurar si no hay sesión de Supabase activa
    const supabaseSession = localStorage.getItem('sb-zldkeibjkpjfrsxzogln-auth-token');
    if (!supabaseSession) {
      console.log('🚫 [Restoration] No Supabase session found, clearing app state');
      // Limpiar estado si no hay sesión
      localStorage.removeItem('app-complete-state');
      localStorage.removeItem('app-state-timestamp');
      sessionStorage.removeItem('app-complete-state_backup');
      sessionStorage.removeItem('app-state-timestamp_backup');
      return;
    }
    
    // NUEVA LÓGICA: Ejecutar restauración cuando:
    // 1. Estamos en el dashboard (punto de entrada después de login)
    // 2. O es la primera carga de la app en una ruta pública
    const isDashboard = currentPath === '/dashboard';
    const isPublicRoute = currentPath === '/' || 
                         currentPath.startsWith('/auth/') ||
                         currentPath === '/faq' ||
                         currentPath.startsWith('/legal/');
    
    // Verificar si es navegación manual (estado limpiado o muy antiguo)
    const savedState = getAppState();
    const timeSinceLastSave = Date.now() - savedState.timestamp;
    const isManualNavigation = timeSinceLastSave > 5000; // >5 segundos = navegación manual

    // Supresión explícita (ej. botón Home dentro del quiz) — evita rebote
    // cuando useAppStatePreservation reescribe el estado durante el desmontaje.
    let suppressUntil = 0;
    try {
      const raw = sessionStorage.getItem("mblearn:suppress-restoration-until");
      suppressUntil = raw ? parseInt(raw, 10) || 0 : 0;
      if (suppressUntil && Date.now() > suppressUntil) {
        sessionStorage.removeItem("mblearn:suppress-restoration-until");
        suppressUntil = 0;
      }
    } catch {
      suppressUntil = 0;
    }
    if (suppressUntil && Date.now() <= suppressUntil) {
      console.log("🚫 [Restoration] Suppressed by flag until", new Date(suppressUntil).toISOString());
      return;
    }
    
    // El dashboard NO debe ser trampolín de restauración automática.
    // La restauración solo ocurre desde rutas públicas (entrada inicial a la app).
    // En el dashboard existen CTAs explícitos (ContinueSession, banners) para reanudar.
    const shouldAttemptRestore = isPublicRoute &&
                                !hasRestoredRef.current &&
                                !isManualNavigation;

    
    if (!shouldAttemptRestore) {
      if (isManualNavigation && isDashboard) {
        console.log('🚫 [Restoration] Manual navigation detected, skipping restoration');
      }
      return;
    }

    hasRestoredRef.current = true;

    // Refresh completion cache so the restore decision sees fresh data.
    // Cheap no-op when synced within the last minute.
    const userId = getCurrentUserIdSync();
    void syncCompletedLevelsFromDB(userId);

    // Dar tiempo para que React Router se inicialice
    const timer = setTimeout(() => {
      const savedState = getAppState();
      
      console.log('🔍 [Restoration] ========================================');
      console.log('🔍 [Restoration] Current Path:', currentPath);
      console.log('🔍 [Restoration] Saved Route:', savedState.route);
      console.log('🔍 [Restoration] Timestamp:', savedState.timestamp);
      console.log('🔍 [Restoration] Session Details:', JSON.stringify(savedState.sessionDetails, null, 2));
      console.log('🔍 [Restoration] isQuizActive:', savedState.isQuizActive);
      console.log('🔍 [Restoration] isExamActive:', savedState.isExamActive);
      console.log('🔍 [Restoration] isStudyActive:', savedState.isStudyActive);
      
      // Verificar si hay una ruta válida guardada y un timestamp reciente (< 30 min)
      const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
      
      // NUEVA LÓGICA: Considerar válida si:
      // 1. Tiene sessionDetails (quiz/study/exam activos)
      // O 2. Tiene una ruta válida no-pública
      const hasSessionDetails = savedState.sessionDetails && 
                               (savedState.sessionDetails.category || 
                                savedState.sessionDetails.mode);
      
      const hasValidRoute = savedState.timestamp > thirtyMinutesAgo &&
                           (hasSessionDetails || 
                            (savedState.route && 
                             savedState.route !== '/' &&
                             savedState.route !== '/auth/login' &&
                             savedState.route !== '/auth/register'));

      console.log('🔍 [Restoration] Time check:', {
        now: Date.now(),
        savedTimestamp: savedState.timestamp,
        thirtyMinutesAgo,
        isRecent: savedState.timestamp > thirtyMinutesAgo
      });
      console.log('🔍 [Restoration] hasSessionDetails:', hasSessionDetails);
      console.log('🔍 [Restoration] hasValidRoute:', hasValidRoute);

      // Rutas que NO deben ser restauradas (rutas públicas/auth)
      const isPublicRoute = currentPath === '/' || 
                           currentPath.startsWith('/auth/') ||
                           currentPath === '/faq' ||
                           currentPath.startsWith('/legal/');

      console.log('🔍 [Restoration] isPublicRoute:', isPublicRoute);

      // Construir la ruta de restauración con los detalles de la sesión
      let targetRoute = savedState.route;
      
      // Si tenemos detalles de sesión, construir la ruta específica
      if (savedState.sessionDetails) {
        const details = savedState.sessionDetails;

        // Quiz mode
        if (details.mode === 'quiz' && details.category && details.level) {
          // Structural guard: never restore into a (category, level) that
          // the user has already approved. Purge every artifact instead.
          if (ensureNotCompletedOrPurge(userId, details.category, details.level)) {
            console.log('🛡️ [Restoration] Skipping — quiz target already approved:', details.category, 'L' + details.level);
            return;
          }
          targetRoute = `/quiz/${details.category}/${details.level}`;
        }
        // Review mode
        else if (details.mode === 'review' && details.category) {
          targetRoute = `/review-bank?category=${details.category}`;
        }
        // Study mode
        else if (details.mode === 'study' && details.category) {
          if (
            typeof details.level === 'number' &&
            ensureNotCompletedOrPurge(userId, details.category, details.level)
          ) {
            console.log('🛡️ [Restoration] Skipping — study target already approved');
            return;
          }
          targetRoute = `/study?category=${details.category}`;
        }
        // Exam mode
        else if (details.mode === 'exam') {
          targetRoute = `/exam-simulation`;
        }
      }

      // Restaurar SIEMPRE que:
      // 1. Hay una ruta válida guardada (con sessionDetails)
      // 2. La ruta guardada es diferente a la actual
      // 3. La ruta actual es pública O es el dashboard (punto de entrada común)
      // 4. NO restaurar examen si estamos en el dashboard (salida intencional)
      const isOnDashboard = currentPath === '/dashboard';
      const isExamTarget = targetRoute === '/exam-simulation';
      
      // Solo restaurar desde rutas públicas (entrada inicial a la app).
      // El dashboard nunca auto-restaura: es destino final del refresh.
      const shouldRestore = hasValidRoute &&
                           targetRoute !== currentPath &&
                           isPublicRoute;


      console.log('🔍 [Restoration] Restoration decision:', {
        hasValidRoute,
        hasSessionDetails,
        targetRoute,
        currentPath,
        isDifferent: targetRoute !== currentPath,
        isPublicRoute,
        isOnDashboard,
        isExamTarget,
        shouldRestore
      });
      console.log('🔍 [Restoration] ========================================');

      if (shouldRestore) {
        console.log('✅ [Restoration] RESTORING to:', targetRoute);
        console.log('✅ [Restoration] With details:', JSON.stringify(savedState.sessionDetails, null, 2));
        
        // Mostrar toast solo una vez
        if (!hasShownToastRef.current) {
          // Verificar si es una sesión activa (quiz, exam, study)
          if (savedState.isQuizActive || savedState.isExamActive || savedState.isStudyActive) {
            // Construir mensaje descriptivo
            let description = t("state.continueFromWhereLeft");
            if (savedState.sessionDetails) {
              const details = savedState.sessionDetails;
              if (details.mode === 'quiz' && details.category && details.level) {
                description = `${t(`category.${details.category}`)} - ${t("quiz.currentLevel")} ${details.level}`;
              } else if (details.mode === 'exam') {
                description = t("exam.resuming");
              } else if (details.mode === 'study' && details.category) {
                description = `${t("study.mode")}: ${t(`category.${details.category}`)}`;
              }
            }
            
            toast.success(t("state.sessionRestored"), {
              description,
              duration: 4000,
            });
          }
          hasShownToastRef.current = true;
        }
        
        // Resetear el flag de scroll para que se restaure en la nueva ruta
        scrollRestoredRef.current = false;
        // Usar replace para no agregar a history
        navigate(targetRoute, { replace: true });
      } else {
        console.log('⏭️ [Restoration] Staying at current route:', currentPath);
        // Si nos quedamos en la ruta actual y no es una ruta guardada, limpiar scroll
        if (currentPath !== targetRoute) {
          window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [navigate, location.pathname]);

  return null;
};
